<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import ConversationsAPI from 'dashboard/api/inbox/conversation';
import MessageAPI from 'dashboard/api/inbox/message';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import PlanningInquiryButton from 'dashboard/components/widgets/conversation/PlanningInquiryButton.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const emit = defineEmits(['complete']);
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 24;

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const inbox = computed(() => store.state.onboarding.inbox);
const assistantId = computed(() => store.state.onboarding.assistantId);
const hasAssistant = computed(
  () =>
    Boolean(assistantId.value) || Boolean(store.state.onboarding.assistantName)
);
const channelType = computed(() => store.state.onboarding.channelType);
const emailProvider = computed(
  () => store.state.onboarding.emailProvider || 'forward'
);
const channelConfigEmail = computed(
  () => store.state.onboarding.channelConfig.email?.email || ''
);
const testEmailResult = computed(() => store.state.onboarding.testEmailResult);
const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);

const screen = ref('ready'); // ready | test (email only)
const incomingExpanded = ref(false);
const draftReady = ref(false);
const labelReady = ref(false);
const contact = ref(null);
const previewMessages = ref([]);
const conversationSubject = ref('');
const pollTimedOut = ref(false);
const copiedKey = ref('');
let pollTimer = null;
let pollAttempts = 0;
let activeDisplayId = null;

const contactReady = computed(() => Boolean(contact.value?.id));
const emailSent = computed(() => Boolean(testEmailResult.value));
const contactUrl = computed(() => {
  if (!contact.value?.id) return null;
  return router.resolve(
    accountScopedRoute('contacts_edit', { contactId: contact.value.id })
  ).href;
});

const isEmail = computed(() => {
  if (['email', 'microsoft'].includes(channelType.value)) return true;
  const inboxChannel = inbox.value?.channel_type || '';
  return [
    'Channel::Email',
    'Channel::Microsoft',
    'email',
    'microsoft',
  ].includes(inboxChannel);
});

const showTestLayout = computed(
  () => isEmail.value && screen.value === 'test'
);

const isSendingTestEmail = computed(() =>
  Boolean(uiFlags.value.isSendingTestEmail)
);

const highlightCreateInquiry = computed(
  () =>
    showTestLayout.value &&
    Boolean(testEmailResult.value?.conversation_id) &&
    !hasAssistant.value
);

const isOauthEmail = computed(() => {
  if (!isEmail.value) return false;
  if (['gmail', 'outlook'].includes(emailProvider.value)) return true;
  if (channelType.value === 'microsoft') return true;
  if (inbox.value?.channel_type === 'Channel::Microsoft') return true;
  if (inbox.value?.provider === 'google') return true;
  return false;
});

const isGmailProvider = computed(() => {
  if (emailProvider.value === 'gmail') return true;
  if (inbox.value?.provider === 'google') return true;
  return false;
});

const isOutlookProvider = computed(() => {
  if (emailProvider.value === 'outlook') return true;
  if (channelType.value === 'microsoft') return true;
  if (inbox.value?.channel_type === 'Channel::Microsoft') return true;
  return false;
});

const isForwardEmail = computed(() => isEmail.value && !isOauthEmail.value);

const connectedEmail = computed(
  () => inbox.value?.email || channelConfigEmail.value || ''
);

const testEmailTo = computed(() => {
  if (isForwardEmail.value) {
    return inbox.value?.forward_to_email || connectedEmail.value;
  }
  return connectedEmail.value;
});

const testComplete = computed(() => {
  if (!emailSent.value) return false;
  if (hasAssistant.value) return draftReady.value;
  return labelReady.value && contactReady.value;
});

const finishCtaLabel = computed(() =>
  inbox.value?.id
    ? t('ONBOARDING_WIZARD.FINISH.CTA')
    : t('ONBOARDING_WIZARD.FINISH.CTA_COPILOT')
);

const stepTitle = computed(() => {
  if (isEmail.value && screen.value === 'test') {
    return t('ONBOARDING_WIZARD.FINISH.TEST_TITLE');
  }
  return t('ONBOARDING_WIZARD.FINISH.TITLE');
});

const stepDescription = computed(() => {
  if (!isEmail.value) {
    return t('ONBOARDING_WIZARD.FINISH.DESCRIPTION_WIDGET');
  }
  if (screen.value === 'test') {
    if (hasAssistant.value) {
      return t('ONBOARDING_WIZARD.FINISH.EMAIL_ASSISTANT_TEST_HINT');
    }
    return isOauthEmail.value
      ? t('ONBOARDING_WIZARD.FINISH.OAUTH_TEST_HINT')
      : t('ONBOARDING_WIZARD.FINISH.EMAIL_TEST_HINT');
  }
  if (hasAssistant.value) {
    return t('ONBOARDING_WIZARD.FINISH.DESCRIPTION_ASSISTANT');
  }
  return t('ONBOARDING_WIZARD.FINISH.DESCRIPTION');
});

function goToReadyScreen() {
  store.commit('onboarding/SET_ERROR', null);
  screen.value = 'ready';
}

function resetTestState() {
  stopPolling();
  pollAttempts = 0;
  activeDisplayId = null;
  incomingExpanded.value = false;
  draftReady.value = false;
  labelReady.value = false;
  contact.value = null;
  previewMessages.value = [];
  conversationSubject.value = '';
  pollTimedOut.value = false;
  store.commit('onboarding/SET_TEST_EMAIL_RESULT', null);
}

function isCollapsedIncoming(message) {
  return (
    hasAssistant.value &&
    isIncoming(message) &&
    !isPrivateNote(message) &&
    !incomingExpanded.value
  );
}

async function startTestEmail({ force = false } = {}) {
  store.commit('onboarding/SET_ERROR', null);
  screen.value = 'test';
  if (force || !testEmailResult.value) {
    if (force) resetTestState();
    await sendTestEmail();
  }
}

useRegisterOnboardingNav(() => {
  if (!isEmail.value) {
    return {
      primary: {
        label: finishCtaLabel.value,
        onClick: () => emit('complete'),
      },
    };
  }

  if (screen.value === 'ready') {
    return {
      primary: {
        label: t('ONBOARDING_WIZARD.FINISH.READY_CTA'),
        loading: uiFlags.value.isSendingTestEmail,
        onClick: startTestEmail,
      },
    };
  }

  if (!testEmailResult.value || isSendingTestEmail.value) {
    return {
      primary: {
        label: t('ONBOARDING_WIZARD.FINISH.SENDING_TEST_EMAIL'),
        loading: true,
        disabled: true,
      },
      onBack: goToReadyScreen,
    };
  }

  return {
    primary: {
      label: finishCtaLabel.value,
      onClick: () => emit('complete'),
    },
    onBack: goToReadyScreen,
  };
});

onMounted(async () => {
  await store.dispatch('onboarding/fetchStatus', { syncStep: false });

  // Keep channelType in sync with the connected inbox (existing-channel AI path).
  if (isEmail.value && !['email', 'microsoft'].includes(channelType.value)) {
    store.dispatch(
      'onboarding/setChannelType',
      inbox.value?.channel_type === 'Channel::Microsoft' ? 'microsoft' : 'email'
    );
  }

  if (!isEmail.value) {
    const token =
      inbox.value?.website_token || inbox.value?.websiteToken || null;
    if (!token) {
      await store.dispatch('onboarding/fetchStatus', { syncStep: false });
    }
    return;
  }

  // AI phase: always send a fresh test email so we can show Roo's draft.
  if (hasAssistant.value) {
    await startTestEmail({ force: true });
    return;
  }

  if (testEmailResult.value?.conversation_id) {
    screen.value = 'test';
    startConversationPolling(testEmailResult.value.conversation_id);
  }
});

onBeforeUnmount(() => {
  stopPolling();
});

watch(testComplete, complete => {
  if (complete) stopPolling();
});

async function sendTestEmail() {
  pollTimedOut.value = false;
  const result = await store.dispatch('onboarding/sendTestEmail', {
    variant: hasAssistant.value ? 'vague' : 'complete',
  });
  if (result?.conversation_id) {
    startConversationPolling(result.conversation_id);
  }
}

async function pollConversationStatus(displayId) {
  try {
    const { data: listData } = await ConversationsAPI.get({
      inboxId: inbox.value.id,
      status: 'all',
    });
    const conversation = (listData.data?.payload || []).find(
      item => item.id === displayId
    );
    if (conversation?.meta?.sender) {
      contact.value = conversation.meta.sender;
    }
    conversationSubject.value =
      conversation?.additional_attributes?.mail_subject ||
      conversation?.meta?.channel ||
      testEmailResult.value?.subject ||
      '';
    if (Array.isArray(conversation?.labels) && conversation.labels.length) {
      labelReady.value = true;
    }
    const { data: messagesData } = await MessageAPI.getPreviousMessages({
      conversationId: displayId,
    });
    const messages = messagesData.payload || messagesData.data?.payload || [];
    previewMessages.value = messages;
    if (
      !labelReady.value &&
      messages.some(
        message =>
          isActivity(message) &&
          /added .+label|added \S+/i.test(messageBody(message))
      )
    ) {
      labelReady.value = true;
    }
    if (hasAssistant.value) {
      draftReady.value = messages.some(message => message.private);
    }

    if (testComplete.value) {
      stopPolling();
      pollTimedOut.value = false;
      return;
    }
  } catch {
    // Keep polling; surfaced via timeout state.
  }

  pollAttempts += 1;
  if (pollAttempts >= MAX_POLL_ATTEMPTS) {
    stopPolling();
    pollTimedOut.value = true;
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function startConversationPolling(displayId) {
  stopPolling();
  activeDisplayId = displayId;
  pollAttempts = 0;
  pollTimedOut.value = false;
  pollConversationStatus(displayId);
  pollTimer = setInterval(
    () => pollConversationStatus(displayId),
    POLL_INTERVAL_MS
  );
}

function retryStatusCheck() {
  if (!activeDisplayId && testEmailResult.value?.conversation_id) {
    activeDisplayId = testEmailResult.value.conversation_id;
  }
  if (!activeDisplayId) return;
  startConversationPolling(activeDisplayId);
}

const widgetPreviewUrl = computed(() => {
  const token = inbox.value?.website_token || inbox.value?.websiteToken;
  if (!token) return null;
  const url = new URL('/widget/dialogue', window.location.origin);
  url.searchParams.set('website_token', token);
  url.searchParams.set('embed', 'true');
  return url.toString();
});

function openExternal(url) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function messageBody(message) {
  const raw =
    message?.content ||
    message?.processed_message_content ||
    message?.content_attributes?.email?.subject ||
    '';
  return String(raw).trim();
}

function isIncoming(message) {
  return (
    message?.message_type === MESSAGE_TYPE.INCOMING ||
    message?.message_type === 'incoming'
  );
}

function isActivity(message) {
  return (
    message?.message_type === MESSAGE_TYPE.ACTIVITY ||
    message?.message_type === 'activity'
  );
}

function isPrivateNote(message) {
  return Boolean(message?.private);
}

const visiblePreviewMessages = computed(() =>
  previewMessages.value.filter(message => {
    if (isPrivateNote(message)) return true;
    if (isActivity(message)) return Boolean(messageBody(message));
    return Boolean(messageBody(message));
  })
);

const showConversationSkeleton = computed(
  () =>
    showTestLayout.value &&
    (!testEmailResult.value?.conversation_id ||
      (!visiblePreviewMessages.value.length &&
        (isSendingTestEmail.value ||
          (hasAssistant.value ? !draftReady.value : !contactReady.value))))
);

const previewSubject = computed(
  () =>
    conversationSubject.value ||
    testEmailResult.value?.subject ||
    t(
      hasAssistant.value
        ? 'ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW_VAGUE.SUBJECT_VALUE'
        : 'ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.SUBJECT_VALUE'
    )
);

const previewEmailSubject = computed(() =>
  t(
    hasAssistant.value
      ? 'ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW_VAGUE.SUBJECT_VALUE'
      : 'ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.SUBJECT_VALUE'
  )
);

const previewEmailBody = computed(() =>
  t(
    hasAssistant.value
      ? 'ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW_VAGUE.BODY'
      : 'ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.BODY'
  )
);

async function copyText(value, key) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    copiedKey.value = key;
    useAlert(t('ONBOARDING_WIZARD.COPIED'));
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = '';
    }, 2000);
  } catch {
    useAlert(t('ONBOARDING_WIZARD.COPY'));
  }
}
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.PHASES.4.LABEL')"
    :title="stepTitle"
    :description="stepDescription"
    :extra-wide="showTestLayout"
  >
    <div class="w-full flex flex-col gap-8">
      <!-- Email ready: preview the sample inquiry we'll send -->
      <template v-if="isEmail && screen === 'ready'">
        <div
          class="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 overflow-hidden"
        >
          <div
            class="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3"
          >
            <p
              class="m-0 text-sm font-medium text-slate-500 dark:text-slate-400"
            >
              {{ t('ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.LABEL') }}
            </p>
            <Icon
              icon="i-lucide-mail"
              class="size-4 text-slate-400 dark:text-slate-500"
            />
          </div>
          <div class="px-4 py-4 flex flex-col gap-3 text-sm">
            <div class="flex gap-3 min-w-0">
              <span
                class="shrink-0 w-16 text-slate-500 dark:text-slate-400"
              >
                {{ t('ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.FROM') }}
              </span>
              <p class="m-0 min-w-0 text-slate-900 dark:text-white break-all">
                <span class="font-medium">{{
                  t('ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.FROM_NAME')
                }}</span>
                <span class="text-slate-500 dark:text-slate-400">
                  &lt;{{
                    t('ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.FROM_EMAIL')
                  }}&gt;
                </span>
              </p>
            </div>
            <div v-if="testEmailTo" class="flex gap-3 min-w-0">
              <span
                class="shrink-0 w-16 text-slate-500 dark:text-slate-400"
              >
                {{ t('ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.TO') }}
              </span>
              <p
                class="m-0 min-w-0 font-medium text-slate-900 dark:text-white break-all"
              >
                {{ testEmailTo }}
              </p>
            </div>
            <div class="flex gap-3 min-w-0">
              <span
                class="shrink-0 w-16 text-slate-500 dark:text-slate-400"
              >
                {{ t('ONBOARDING_WIZARD.FINISH.TEST_EMAIL_PREVIEW.SUBJECT') }}
              </span>
              <p
                class="m-0 min-w-0 font-medium text-slate-900 dark:text-white"
              >
                {{ previewEmailSubject }}
              </p>
            </div>
            <div
              class="mt-1 pt-4 border-t border-slate-100 dark:border-slate-800"
            >
              <p
                class="m-0 whitespace-pre-wrap text-slate-700 dark:text-slate-200 leading-relaxed"
              >
                {{ previewEmailBody }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- Email test screen -->
      <template v-else-if="showTestLayout">
        <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
          {{ error }}
        </p>

        <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="flex flex-col gap-4 min-w-0">
            <p
              class="m-0 text-lg font-semibold text-slate-900 dark:text-white"
            >
              {{ t('ONBOARDING_WIZARD.FINISH.TEST_STATUS') }}
            </p>
            <ul class="w-full flex flex-col gap-3 m-0 p-0 list-none">
              <li class="flex items-center gap-4 py-1">
                <div
                  class="size-10 shrink-0 rounded-full flex items-center justify-center"
                  :class="
                    emailSent
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-woot-50 dark:bg-woot-900/30'
                  "
                >
                  <Icon
                    v-if="emailSent"
                    icon="i-lucide-check"
                    class="size-5 text-green-600 dark:text-green-400"
                  />
                  <span
                    v-else
                    class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
                  />
                </div>
                <p
                  class="m-0 text-base font-medium text-slate-900 dark:text-white"
                >
                  {{
                    emailSent
                      ? t('ONBOARDING_WIZARD.FINISH.EMAIL_SENT')
                      : t('ONBOARDING_WIZARD.FINISH.EMAIL_SENDING')
                  }}
                </p>
              </li>
              <li v-if="!hasAssistant" class="flex items-center gap-4 py-1">
                <div
                  class="size-10 shrink-0 rounded-full flex items-center justify-center"
                  :class="
                    labelReady
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-woot-50 dark:bg-woot-900/30'
                  "
                >
                  <Icon
                    v-if="labelReady"
                    icon="i-lucide-check"
                    class="size-5 text-green-600 dark:text-green-400"
                  />
                  <span
                    v-else-if="emailSent && !pollTimedOut"
                    class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
                  />
                  <Icon
                    v-else-if="pollTimedOut"
                    icon="i-lucide-clock"
                    class="size-5 text-amber-600 dark:text-amber-400"
                  />
                  <span
                    v-else
                    class="size-4 rounded-full border border-slate-300 dark:border-slate-600"
                  />
                </div>
                <p
                  class="m-0 text-base font-medium text-slate-900 dark:text-white"
                >
                  {{
                    labelReady
                      ? t('ONBOARDING_WIZARD.FINISH.LABEL_APPLIED')
                      : t('ONBOARDING_WIZARD.FINISH.LABEL_PENDING')
                  }}
                </p>
              </li>
              <li v-if="!hasAssistant" class="flex items-center gap-4 py-1">
                <div
                  class="size-10 shrink-0 rounded-full flex items-center justify-center"
                  :class="
                    contactReady
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-woot-50 dark:bg-woot-900/30'
                  "
                >
                  <Icon
                    v-if="contactReady"
                    icon="i-lucide-check"
                    class="size-5 text-green-600 dark:text-green-400"
                  />
                  <span
                    v-else-if="emailSent && !pollTimedOut"
                    class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
                  />
                  <Icon
                    v-else-if="pollTimedOut"
                    icon="i-lucide-clock"
                    class="size-5 text-amber-600 dark:text-amber-400"
                  />
                  <span
                    v-else
                    class="size-4 rounded-full border border-slate-300 dark:border-slate-600"
                  />
                </div>
                <p
                  class="m-0 text-base font-medium text-slate-900 dark:text-white"
                >
                  {{
                    contactReady
                      ? t('ONBOARDING_WIZARD.FINISH.CONTACT_CREATED')
                      : t('ONBOARDING_WIZARD.FINISH.CONTACT_PENDING')
                  }}
                </p>
              </li>
              <li v-if="hasAssistant" class="flex items-center gap-4 py-1">
                <div
                  class="size-10 shrink-0 rounded-full flex items-center justify-center"
                  :class="
                    draftReady
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-woot-50 dark:bg-woot-900/30'
                  "
                >
                  <Icon
                    v-if="draftReady"
                    icon="i-lucide-check"
                    class="size-5 text-green-600 dark:text-green-400"
                  />
                  <span
                    v-else-if="emailSent && !pollTimedOut"
                    class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
                  />
                  <Icon
                    v-else-if="pollTimedOut"
                    icon="i-lucide-clock"
                    class="size-5 text-amber-600 dark:text-amber-400"
                  />
                  <span
                    v-else
                    class="size-4 rounded-full border border-slate-300 dark:border-slate-600"
                  />
                </div>
                <p
                  class="m-0 text-base font-medium text-slate-900 dark:text-white"
                >
                  {{
                    draftReady
                      ? t('ONBOARDING_WIZARD.FINISH.DRAFT_READY')
                      : t('ONBOARDING_WIZARD.FINISH.DRAFT_PENDING')
                  }}
                </p>
              </li>
            </ul>

            <p
              v-if="testComplete"
              class="text-sm text-slate-600 dark:text-slate-300 m-0"
            >
              {{ t('ONBOARDING_WIZARD.FINISH.TEST_COMPLETE') }}
            </p>
            <div v-else-if="pollTimedOut" class="flex flex-col gap-3">
              <p class="text-sm text-amber-700 dark:text-amber-400 m-0">
                {{ t('ONBOARDING_WIZARD.FINISH.TEST_TIMEOUT') }}
              </p>
              <Button
                md
                slate
                outline
                icon="i-lucide-refresh-cw"
                :label="t('ONBOARDING_WIZARD.FINISH.RETRY_STATUS')"
                @click="retryStatusCheck"
              />
            </div>

            <div
              v-if="testComplete && testEmailResult"
              class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2"
            >
              <button
                v-if="testEmailResult.conversation_url"
                type="button"
                class="rounded-2xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70 p-5 text-left shadow-sm transition-all hover:border-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20"
                @click="openExternal(testEmailResult.conversation_url)"
              >
                <Icon
                  icon="i-lucide-message-square"
                  class="size-6 text-woot-500 mb-3"
                />
                <p
                  class="m-0 font-semibold text-slate-900 dark:text-white text-base"
                >
                  {{ t('ONBOARDING_WIZARD.FINISH.OPEN_CONVERSATION') }}
                </p>
              </button>
              <button
                v-if="isGmailProvider && testEmailResult.gmail_url"
                type="button"
                class="rounded-2xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70 p-5 text-left shadow-sm transition-all hover:border-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20"
                @click="openExternal(testEmailResult.gmail_url)"
              >
                <Icon icon="i-woot-gmail" class="size-6 mb-3" />
                <p
                  class="m-0 font-semibold text-slate-900 dark:text-white text-base"
                >
                  {{ t('ONBOARDING_WIZARD.FINISH.OPEN_GMAIL') }}
                </p>
              </button>
              <button
                v-if="isOutlookProvider && testEmailResult.outlook_url"
                type="button"
                class="rounded-2xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70 p-5 text-left shadow-sm transition-all hover:border-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20"
                @click="openExternal(testEmailResult.outlook_url)"
              >
                <Icon icon="i-woot-outlook" class="size-6 mb-3" />
                <p
                  class="m-0 font-semibold text-slate-900 dark:text-white text-base"
                >
                  {{ t('ONBOARDING_WIZARD.FINISH.OPEN_OUTLOOK') }}
                </p>
              </button>
            </div>
          </div>

          <div
            class="min-w-0 flex flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 overflow-hidden h-[560px]"
          >
            <div
              class="shrink-0 flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800"
            >
              <div class="min-w-0 flex items-center gap-3">
                <div
                  v-if="showConversationSkeleton && !contact"
                  class="size-9 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse shrink-0"
                />
                <Avatar
                  v-else
                  :name="
                    contact?.name ||
                      t('ONBOARDING_WIZARD.FINISH.CONVERSATION_PREVIEW')
                  "
                  :src="contact?.thumbnail || ''"
                  :size="36"
                  rounded-full
                  hide-offline-status
                />
                <div class="min-w-0 flex-1">
                  <p
                    v-if="showConversationSkeleton && !contact"
                    class="m-0 h-4 w-28 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"
                  />
                  <p
                    v-else
                    class="m-0 text-sm font-semibold text-slate-900 dark:text-white truncate"
                  >
                    {{
                      contact?.name ||
                        t('ONBOARDING_WIZARD.FINISH.CONVERSATION_PREVIEW')
                    }}
                  </p>
                  <p
                    v-if="showConversationSkeleton && !conversationSubject"
                    class="m-0 mt-1.5 h-3 w-40 rounded bg-slate-100 dark:bg-slate-800 animate-pulse"
                  />
                  <p
                    v-else-if="previewSubject"
                    class="m-0 text-xs text-slate-500 dark:text-slate-400 truncate"
                  >
                    {{ previewSubject }}
                  </p>
                </div>
              </div>
              <div class="shrink-0 flex items-center gap-2">
                <Button
                  v-if="contactUrl"
                  sm
                  slate
                  outline
                  icon="i-lucide-external-link"
                  :label="t('ONBOARDING_WIZARD.FINISH.CONTACT_VIEW')"
                  @click="openExternal(contactUrl)"
                />
                <PlanningInquiryButton
                  v-if="
                    !hasAssistant && testEmailResult?.conversation_id
                  "
                  :conversation-id="testEmailResult.conversation_id"
                  :highlighted="highlightCreateInquiry"
                />
                <div
                  v-else-if="!hasAssistant"
                  class="h-9 w-36 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse"
                />
              </div>
            </div>

            <div class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
              <template v-if="showConversationSkeleton">
                <div class="flex justify-start">
                  <div
                    class="w-[85%] h-24 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                  />
                </div>
                <div class="flex justify-center py-1">
                  <div
                    class="h-3 w-48 rounded bg-slate-100 dark:bg-slate-800 animate-pulse"
                  />
                </div>
                <div class="flex justify-start">
                  <div
                    class="w-[55%] h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
                  />
                </div>
              </template>
              <template v-else>
                <div
                  v-for="message in visiblePreviewMessages"
                  :key="message.id"
                  class="flex"
                  :class="
                    isActivity(message)
                      ? 'justify-center'
                      : isIncoming(message) || isPrivateNote(message)
                        ? 'justify-start'
                        : 'justify-end'
                  "
                >
                  <p
                    v-if="isActivity(message)"
                    class="m-0 max-w-[90%] text-center text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{ messageBody(message) }}
                  </p>
                  <div
                    v-else
                    class="max-w-[90%] rounded-2xl px-3.5 py-2.5 text-sm break-words"
                    :class="[
                      isPrivateNote(message)
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-950 dark:text-amber-100 border border-amber-100 dark:border-amber-800/40'
                        : isIncoming(message)
                          ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                          : 'bg-woot-500 text-white',
                      isCollapsedIncoming(message)
                        ? 'cursor-pointer'
                        : 'whitespace-pre-wrap',
                    ]"
                    @click="
                      isCollapsedIncoming(message) &&
                        (incomingExpanded = true)
                    "
                  >
                    <p
                      v-if="isPrivateNote(message)"
                      class="m-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
                    >
                      {{ t('ONBOARDING_WIZARD.FINISH.PREVIEW_PRIVATE_NOTE') }}
                    </p>
                    <p
                      class="m-0"
                      :class="
                        isCollapsedIncoming(message) ? 'line-clamp-2' : ''
                      "
                    >
                      {{ messageBody(message) || '—' }}
                    </p>
                    <button
                      v-if="
                        hasAssistant &&
                        isIncoming(message) &&
                        !isPrivateNote(message)
                      "
                      type="button"
                      class="mt-1.5 p-0 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-transparent border-0 cursor-pointer"
                      @click.stop="incomingExpanded = !incomingExpanded"
                    >
                      {{
                        incomingExpanded
                          ? t('ONBOARDING_WIZARD.FINISH.PREVIEW_SHOW_LESS')
                          : t('ONBOARDING_WIZARD.FINISH.PREVIEW_SHOW_MORE')
                      }}
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <!-- Website widget -->
      <template v-else>
        <div
          v-if="inbox?.web_widget_script"
          class="flex flex-col gap-3"
        >
          <div class="flex items-center justify-between gap-3">
            <p class="m-0 text-sm font-medium text-slate-500 dark:text-slate-400">
              {{ t('ONBOARDING_WIZARD.FINISH.WIDGET_EMBED') }}
            </p>
            <Button
              sm
              slate
              outline
              icon="i-lucide-copy"
              :label="
                copiedKey === 'script'
                  ? t('ONBOARDING_WIZARD.COPIED')
                  : t('ONBOARDING_WIZARD.FINISH.COPY_SCRIPT')
              "
              @click="copyText(inbox.web_widget_script, 'script')"
            />
          </div>
          <pre
            class="text-xs bg-slate-50 dark:bg-slate-800 p-4 rounded-xl overflow-x-auto whitespace-pre-wrap border border-slate-100 dark:border-slate-700 m-0"
            >{{ inbox.web_widget_script }}</pre>
        </div>

        <div
          class="w-full rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden"
        >
          <iframe
            v-if="widgetPreviewUrl"
            :src="widgetPreviewUrl"
            :title="t('ONBOARDING_WIZARD.FINISH.WIDGET_PREVIEW_TITLE')"
            class="w-full h-[560px] bg-white border-0 block"
            allow="microphone; camera; geolocation"
          />
          <p v-else class="text-sm text-slate-500 dark:text-slate-400 p-6 m-0">
            {{ t('ONBOARDING_WIZARD.FINISH.WIDGET_MISSING') }}
          </p>
        </div>
      </template>
    </div>
  </OnboardingStepLayout>
</template>
